import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiClientError } from '../../services/api';
import type { MediaGenerationSummary } from '../../types';

type ProviderInfo = {
  value: string;
  label: string;
  models: string[];
};

type StatusResponse = {
  summary: MediaGenerationSummary | null;
  availableProviders: ProviderInfo[];
  defaultModel: Record<string, string>;
};

const MediaGenerationSettings = () => {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [action, setAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await api.mediaGenerationStatus();
      setStatus(data);
      const current = data.summary;
      if (current) {
        setSelectedProvider(current.provider);
        setSelectedModel(current.model);
      } else {
        const first = data.availableProviders[0];
        if (first) {
          setSelectedProvider(first.value);
          setSelectedModel(data.defaultModel[first.value] ?? first.models[0] ?? '');
        }
      }
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Kon status media-generatie niet ophalen.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void reload();
  }, [reload]);

  const handleProviderChange = (value: string) => {
    setSelectedProvider(value);
    if (status) {
      const info = status.availableProviders.find((p) => p.value === value);
      const fallback = status.defaultModel[value] ?? info?.models[0] ?? '';
      setSelectedModel(fallback);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Voer een API-key in.');
      return;
    }
    setAction('save');
    setError(null);
    setNotice(null);
    try {
      await api.saveMediaGenerationCredentials({
        provider: selectedProvider,
        apiKey: apiKey.trim(),
        model: selectedModel,
      });
      setApiKey('');
      setNotice('API-key voor media-generatie veilig opgeslagen.');
      void reload();
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'Opslaan mislukt.');
    } finally {
      setAction(null);
    }
  };

  const handleTest = async () => {
    setAction('test');
    setError(null);
    setNotice(null);
    try {
      const result = await api.testMediaGeneration(selectedProvider);
      if (result.success) {
        setNotice(`Test geslaagd in ${result.latencyMs}ms.`);
      } else {
        const detail = result.errorMessage
          ? `${result.errorCode ?? 'fout'}: ${result.errorMessage}`
          : 'Test mislukt';
        setError(`Test mislukt (${result.latencyMs}ms): ${detail}`);
      }
      void reload();
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'Testen mislukt.');
    } finally {
      setAction(null);
    }
  };

  const handleRemove = async () => {
    setAction('remove');
    setError(null);
    setNotice(null);
    try {
      await api.removeMediaGenerationCredentials(selectedProvider);
      setNotice('Media-generatie credentials verwijderd.');
      void reload();
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'Verwijderen mislukt.');
    } finally {
      setAction(null);
    }
  };

  if (loading) {
    return (
      <section className="settings-card">
        <h2>Media-generatie (foto/video voor compose)</h2>
        <p className="muted">Laden…</p>
      </section>
    );
  }

  const currentModels = status?.availableProviders.find((p) => p.value === selectedProvider)?.models ?? [];
  const existing = status?.summary;
  const statusText = !existing
    ? 'Niet geconfigureerd'
    : existing.testStatus === 'passed'
      ? 'Test geslaagd'
      : existing.testStatus === 'failed'
        ? 'Test mislukt'
        : 'Geconfigureerd';

  return (
    <section className="settings-card">
      <div className="setting-heading">
        <div>
          <h2>Media-generatie (foto/video voor compose)</h2>
          <p>
            Provider: <strong>{status?.summary?.provider ?? 'geen'}</strong> —{' '}
            <span className={`status-pill ${status?.summary?.testStatus === 'passed' ? 'success' : 'warning'}`}>
              {statusText}
            </span>
          </p>
        </div>
      </div>

      {existing?.lastError && <p className="notice warning">{existing.lastError}</p>}

      <p className="muted">
        API-key voor het genereren van foto's en video's in de composer. Wordt versleuteld opgeslagen op de server.
      </p>

      <div className="form-group">
        <label htmlFor="media-generation-provider">Provider</label>
        <select
          id="media-generation-provider"
          value={selectedProvider}
          onChange={(event) => handleProviderChange(event.target.value)}
          disabled={action !== null}
        >
          {status?.availableProviders.map((provider) => (
            <option key={provider.value} value={provider.value}>
              {provider.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="media-generation-model">Model</label>
        <select
          id="media-generation-model"
          value={selectedModel}
          onChange={(event) => setSelectedModel(event.target.value)}
          disabled={action !== null}
        >
          {currentModels.map((model) => (
            <option key={model} value={model}>
              {model}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="media-generation-api-key">API-key</label>
        <input
          id="media-generation-api-key"
          type="password"
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          placeholder={existing ? 'Vul in om te wijzigen' : 'Plak je OpenAI API-key'}
          disabled={action !== null}
        />
      </div>

      {error && <p className="notice warning">{error}</p>}
      {notice && <p className="notice">{notice}</p>}

      <div className="button-row">
        <button
          type="button"
          className="button primary small"
          onClick={() => void handleSave()}
          disabled={action !== null}
        >
          {action === 'save' ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button
          type="button"
          className="button secondary small"
          onClick={() => void handleTest()}
          disabled={action !== null || !existing}
        >
          {action === 'test' ? 'Testen…' : 'Test verbinding'}
        </button>
        <button
          type="button"
          className="button danger small"
          onClick={() => void handleRemove()}
          disabled={action !== null || !existing}
        >
          {action === 'remove' ? 'Verwijderen…' : 'Verwijderen'}
        </button>
      </div>
    </section>
  );
};

export default MediaGenerationSettings;
